<template>
    <app-layout title="Edit Chain">
        <form @submit.prevent="submit">
            <page-header>
                <template #title>Edit Chain</template>

                <template #actions>
                    <jet-button v-show="form.isDirty" type="submit" :disabled="form.processing">
                        Save
                    </jet-button>

                    <jet-tertiary-button
                        v-if="form.isDirty"
                        :disabled="form.processing"
                        @click="showUnsavedChangesModal = true"
                    >
                        Cancel
                    </jet-tertiary-button>

                    <jet-tertiary-button
                        v-else
                        :disabled="form.processing"
                        :href="backUrl"
                    >
                        Close
                    </jet-tertiary-button>
                </template>
            </page-header>

            <div class="px-8 xl:flex">
                <chain-customer-form
                    :form="form"
                    :customers="customers"
                />

                <chain-customer-pricing-form
                    :form="form"
                    :pricing-products-url="route('chains.pricing_products')"
                    :chain-id="chain.id"
                />
            </div>

            <div class="w-full text-center my-8">
                <jet-danger-button @click="showDeleteModal = true">
                    Delete Chain
                </jet-danger-button>
            </div>
        </form>

        <unsaved-changes-modal-2
            :formRoute="formRoute"
            :isDirty="form.isDirty"
            :show="showUnsavedChangesModal"
            @close="showUnsavedChangesModal = false"
            @confirm="$inertia.get(backUrl)"
        />

        <jet-confirmation-modal :show="showDeleteModal" @close="showDeleteModal=false">
            <template #title>Delete Chain</template>

            <template #content>
                Are you sure you want to delete this chain?
            </template>

            <template #footer>
                <jet-tertiary-button @click="showDeleteModal=false">
                    Cancel
                </jet-tertiary-button>

                <jet-danger-button
                    class="ml-2"
                    @click="deleteChain"
                    :disabled="form.processing"
                >
                    Delete
                </jet-danger-button>
            </template>
        </jet-confirmation-modal>
    </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import PageHeader from '@/Layouts/partials/PageHeader.vue';
import JetButton from '@/Jetstream/Button';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import UnsavedChangesModal2 from '@/Shared/UnsavedChangesModal2';

import ChainCustomerForm from '@/Pages/Chains/Partials/ChainCustomerForm';
import ChainCustomerPricingForm from '@/Pages/Chains/Partials/ChainCustomerPricingForm';

import '@vueform/multiselect/themes/default.css';
import '@vueform/toggle/themes/default.css';

// clone favourites object so that local edits don't affect actual data until "save"
function cloneChainFavouritesMap (src) {
    const out = {};
    if (!src || typeof src !== 'object') {
        return out;
    }
    for (const key of Object.keys(src)) {
        const arr = src[key];
        if (Array.isArray(arr)) {
            out[String(key)] = arr.map(id => Number(id));
        }
    }
    return out;
}

export default {
    setup(props) {
        const form = useForm({
            id: props.chain.id,
            code: props.chain.code,
            name: props.chain.name,
            address_1: props.chain.address_1,
            address_2: props.chain.address_2,
            city: props.chain.city,
            postal_code: props.chain.postal_code,
            province: props.chain.province,
            phone: props.chain.phone,
            email: props.chain.email,
            contact_name: props.chain.contact_name,
            customers: props.chain.customers,
            pricing: props.chain.pricing.map(p => ({
                product_id: p.product_id,
                price: p.price,
                base_price: p.product.price,
                cost: p.product.cost,
                market_cost: p.product.market_cost,
                title: p.product.title,
                size: p.product.size,
                modifier_type: p.modifier_type,
                modifier_amount: p.modifier_amount,
            })),
            favourites_by_product: cloneChainFavouritesMap(props.favouritesByProduct),
            customer_favourites_visible_only: !!props.chain.customer_favourites_visible_only,
        });

        return { form };
    },

    components: {
        AppLayout,
        PageHeader,
        JetButton,
        JetDangerButton,
        JetTertiaryButton,
        JetConfirmationModal,
        UnsavedChangesModal2,
        ChainCustomerForm,
        ChainCustomerPricingForm,
    },

    props: [
        'chain',
        'customers',
        'favouritesByProduct',
    ],

    data() {
        return {
            showDeleteModal: false,
            showUnsavedChangesModal: false,
            formRoute: this.route('chains.update', this.chain.id),
        };
    },

    computed: {
        backUrl() {
            return route('chains.index');
        },
    },

    methods: {
        submit() {
            this.form
                .transform(data => ({
                    ...data,
                    customers: data.customers.map(c => c.id),
                }))
                .submit('put', route('chains.update', this.chain.id));
        },

        deleteChain() {
            this.$inertia.delete(route('chains.destroy', this.chain.id));
        },
    },
}
</script>

<style scoped>
</style>
